import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@weave-mui/material';
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import Typography, {
  typographyVariants,
  typographyFontWeight,
} from '@weave-mui/typography';
import { EditM as EditIcon, TrashM as TrashIcon } from '@weave-mui/icons-weave';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

/**
 * Component to display a list of global fields
 * @param {Object} props - Component props
 * @param {Array} props.fields - Array of global field objects
 * @param {Function} props.onEdit - Callback function when edit button is clicked
 * @param {Function} props.onDelete - Callback function when delete button is clicked
 */
const GlobalFieldsList = ({ fields = [], onEdit, onDelete }) => {
  const [expandedFields, setExpandedFields] = useState({});

  const iconButtonStyles = {
    color: 'text.primary',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  };

  const toggleExpand = (fieldKey) => {
    setExpandedFields((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  const renderStringField = (field, isLastField = false) => (
    <TableRow
      key={field.key}
      sx={{
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '& td': {
          borderBottom: isLastField ? 'none' : undefined,
        },
      }}
    >
      <TableCell sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant={typographyVariants.BODY}
              fontWeight={typographyFontWeight.MEDIUM}
            >
              {field.key}
            </Typography>
            <Chip
              label="String"
              size="small"
              sx={{
                height: '20px',
                fontSize: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                color: 'text.secondary',
                fontWeight: 500,
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
              }}
            />
          </Box>
          <Typography
            variant={typographyVariants.BODY}
            sx={{
              color: 'text.secondary',
              fontSize: '14px',
              marginTop: '12px',
            }}
          >
            {field.value}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ py: 2, textAlign: 'right', width: '120px' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => onEdit(field)}
              size="small"
              sx={iconButtonStyles}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={() => onDelete(field)}
              size="small"
              sx={iconButtonStyles}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderChoiceField = (field, isLastField = false) => {
    const isExpanded = expandedFields[field.key];
    const choiceCount = field.options?.length || 0;

    return (
      <React.Fragment key={field.key}>
        <TableRow
          sx={{
            backgroundColor: isExpanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            '&:hover': {
              backgroundColor: isExpanded
                ? 'rgba(0, 0, 0, 0.06)'
                : 'rgba(0, 0, 0, 0.04)',
            },
            '& td': {
              borderBottom: isLastField && !isExpanded ? 'none' : undefined,
            },
          }}
        >
          <TableCell sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant={typographyVariants.BODY}
                  fontWeight={typographyFontWeight.MEDIUM}
                >
                  {field.key}
                </Typography>
                <Chip
                  label="Choice"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    color: 'text.secondary',
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      padding: '0 8px',
                    },
                  }}
                />
              </Box>
              <Typography
                variant={typographyVariants.CAPTION}
                sx={{ color: 'text.secondary', marginTop: '12px' }}
              >
                {choiceCount} {choiceCount === 1 ? 'choice' : 'choices'}{' '}
                available
              </Typography>
            </Box>
          </TableCell>
          <TableCell sx={{ py: 2, textAlign: 'right', width: '120px' }}>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => toggleExpand(field.key)}
                size="small"
                sx={iconButtonStyles}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => onEdit(field)}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => onDelete(field)}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                py: 0,
                borderBottom: isLastField
                  ? 'none'
                  : '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    py: 2,
                    pl: 4,
                    pr: 2,
                    maxWidth: '100%',
                    overflowX: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, minmax(200px, 1fr))',
                      gap: 2,
                    }}
                  >
                    {field.options?.map((option, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant={typographyVariants.BODY}
                          sx={{
                            color:
                              field.default === option.value
                                ? 'primary.main'
                                : 'text.primary',
                            fontWeight:
                              field.default === option.value
                                ? typographyFontWeight.MEDIUM
                                : typographyFontWeight.REGULAR,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={`${option.value}${
                            field.default === option.value ? ' (default)' : ''
                          }`}
                        >
                          • {option.value}
                          {field.default === option.value && ' (default)'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        overflowX: 'auto',
      }}
    >
      <Table>
        <TableBody>
          {fields.map((field, index) => {
            const isLastField = index === fields.length - 1;
            return field.type === 'string'
              ? renderStringField(field, isLastField)
              : renderChoiceField(field, isLastField);
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GlobalFieldsList;
